import type { CATEGORIES } from 'enums/categories'
import { PRODUCT_CATEGORIES } from 'enums/categories'

/** Enuygun homepage URL `?ref=` değerlerinin search form sekmelerine eşlemesi */
export const HOMEPAGE_REF_TO_CATEGORY: Record<string, CATEGORIES> = {
  ucak: 'ucak-bileti',
  otel: 'otel',
  otobus: 'otobus-bileti',
  arac: 'arac-kiralama',
  transfer: 'transfer',
  // Uçak+Otel yalnızca eski ref ile açılır
  dynamic_package: 'dinamik',
}

/**
 * SearchForms desktop/mobile sekme sırası.
 * PRODUCT_CATEGORIES sayıları ile birebir uyuşmaz (dinamik/araç/transfer kaymış).
 */
export const HOMEPAGE_SEARCH_TAB_ORDER: CATEGORIES[] = ['ucak-bileti', 'otel', 'otobus-bileti', 'dinamik', 'arac-kiralama', 'transfer']

/**
 * URL `ref` query değerinden PRODUCT_CATEGORIES anahtarını döner.
 * Next.js `string | string[]` olabilir; ilk geçerli değer kullanılır.
 */
export const getCategoryFromHomepageRef = (ref: string | string[] | undefined | null): CATEGORIES | null => {
  if (!ref) return null
  const value = Array.isArray(ref) ? ref[0] : ref
  if (!value) return null
  return HOMEPAGE_REF_TO_CATEGORY[value] ?? null
}

/**
 * Router query veya window.location'dan homepage `ref` değerini okur.
 * Client'ta window öncelikli (router.query gecikmeli gelebilir).
 */
export const resolveHomepageRefParam = (queryRef?: string | string[] | null): string | null => {
  if (typeof globalThis.window !== 'undefined') {
    const fromWindow = new URLSearchParams(globalThis.window.location.search).get('ref')
    if (fromWindow) return fromWindow
  }
  if (!queryRef) return null
  return Array.isArray(queryRef) ? queryRef[0] ?? null : queryRef
}

/**
 * Tab listesinde kategoriye karşılık gelen index'i bulur.
 * PRODUCT_CATEGORIES sayıları ile tab sırası (dinamik/araç/transfer) birebir uyuşmadığı için findIndex / sabit sıra kullanılır.
 */
export const findTabIndexByCategory = (tabs: Array<{ category?: number }>, categoryKey: CATEGORIES): number => {
  const fromTabs = tabs.findIndex((tab) => tab.category === PRODUCT_CATEGORIES[categoryKey])
  if (fromTabs !== -1) return fromTabs
  return HOMEPAGE_SEARCH_TAB_ORDER.indexOf(categoryKey)
}

/** `?ref=` değerinden doğrudan tab index'i (yoksa -1) */
export const getTabIndexFromHomepageRef = (ref?: string | string[] | null): number => {
  const category = getCategoryFromHomepageRef(resolveHomepageRefParam(ref) ?? ref)
  if (!category) return -1
  return HOMEPAGE_SEARCH_TAB_ORDER.indexOf(category)
}

export const savePreviousCategoryBeforeSubmit = (category: string) => {
  return globalThis.window?.localStorage.setItem('previous-search-category', category)
}

export const getPreviousCategoryBeforeShow = (isClient: boolean) => {
  if (isClient) {
    const previousCategory = globalThis.window.localStorage.getItem('previous-search-category')
    if (previousCategory === null) {
      return 0
    }
    return typeof previousCategory === 'string' ? Number.parseInt(previousCategory) || 0 : 0
  }
  return 0
}

const WINGIE_PREVIOUS_SEARCH_CATEGORY_KEY = 'wingie-previous-search-category'

export const WINGIE_SEARCH_FORM_TABS = {
  flight: 0,
  hotel: 1,
  carRental: 2,
} as const

/** Wingie homepage URL `?ref=` → tab index */
export const WINGIE_REF_TO_TAB: Record<string, (typeof WINGIE_SEARCH_FORM_TABS)[keyof typeof WINGIE_SEARCH_FORM_TABS]> = {
  flight: WINGIE_SEARCH_FORM_TABS.flight,
  hotel: WINGIE_SEARCH_FORM_TABS.hotel,
  car: WINGIE_SEARCH_FORM_TABS.carRental,
}

/**
 * Wingie `?ref=flight|hotel|car` değerinden tab index'i.
 * Tanınmayan ref → -1. İstenen sekme kapalıysa flight'a düşer (yine de ref “ele alınmış” sayılır).
 */
export const getWingieTabIndexFromRef = (ref?: string | string[] | null, hotelTabEnabled = true, carRentalTabEnabled = false): number => {
  const resolved = resolveHomepageRefParam(ref)
  if (!resolved) return -1

  const tab = WINGIE_REF_TO_TAB[resolved]
  if (tab === undefined) return -1

  if (tab === WINGIE_SEARCH_FORM_TABS.hotel && !hotelTabEnabled) {
    return WINGIE_SEARCH_FORM_TABS.flight
  }
  if (tab === WINGIE_SEARCH_FORM_TABS.carRental && !carRentalTabEnabled) {
    return WINGIE_SEARCH_FORM_TABS.flight
  }

  return tab
}

/** Wingie search formunda otel sekmesinin gösterileceği diller (ör. us-fr, us-it, us-pl) */
export const WINGIE_HOTEL_TAB_ENABLED_LANGUAGES = ['en', 'ar', 'de', 'es', 'fr', 'it', 'kk', 'ky', 'lt', 'nl', 'pl', 'pt', 'ro', 'ru', 'sr', 'tg', 'uz'] as const

/**
 * Bu market subdomain'lerinde otel sekmesi kapalı (dil fark etmez).
 * Avrupa domainleri (de, gb, es, fr, it, pl) + az.
 * Örn. wingie.pl/en → market=pl, language=en → kapalı.
 */
export const WINGIE_HOTEL_TAB_DISABLED_MARKETS = ['de', 'gb', 'es', 'fr', 'it', 'az', 'pl'] as const

export const isWingieHotelTabEnabled = (language?: string | null, market?: string | null) => {
  // Önce market: kapalı subdomain'lerde dil ne olursa olsun otel tab yok
  if (market && (WINGIE_HOTEL_TAB_DISABLED_MARKETS as readonly string[]).includes(market)) {
    return false
  }

  if (!language) {
    return false
  }

  return (WINGIE_HOTEL_TAB_ENABLED_LANGUAGES as readonly string[]).includes(language)
}

/**
 * Araç kiralama sekmesinin gösterileceği diller (otel ile aynı diller).
 */
export const WINGIE_CAR_RENTAL_TAB_ENABLED_LANGUAGES = WINGIE_HOTEL_TAB_ENABLED_LANGUAGES

/**
 * Araç kiralama sekmesi kapalı olan marketler (dil fark etmez).
 * - Avrupa domainleri (de, gb, es, fr, it, pl): ürün kaldırıldı.
 * - `az`: az.enuygun.com Enuygun markasıdır ve `/cars` akışı yoktur.
 */
export const WINGIE_CAR_RENTAL_TAB_DISABLED_MARKETS = ['az', 'de', 'gb', 'es', 'fr', 'it', 'pl'] as const

export const isWingieCarRentalTabEnabled = (language?: string | null, market?: string | null) => {
  if (market && (WINGIE_CAR_RENTAL_TAB_DISABLED_MARKETS as readonly string[]).includes(market)) {
    return false
  }

  if (!language) {
    return false
  }

  return (WINGIE_CAR_RENTAL_TAB_ENABLED_LANGUAGES as readonly string[]).includes(language as (typeof WINGIE_CAR_RENTAL_TAB_ENABLED_LANGUAGES)[number])
}

export const saveWingiePreviousCategoryBeforeSubmit = (tabIndex: number) => {
  return globalThis.window?.localStorage.setItem(WINGIE_PREVIOUS_SEARCH_CATEGORY_KEY, String(tabIndex))
}

export const getWingiePreviousCategoryBeforeShow = (isClient: boolean, hotelTabEnabled = true, carRentalTabEnabled = false) => {
  if (!isClient) {
    return WINGIE_SEARCH_FORM_TABS.flight
  }

  const previousCategory = globalThis.window.localStorage.getItem(WINGIE_PREVIOUS_SEARCH_CATEGORY_KEY)
  if (previousCategory === null) {
    return WINGIE_SEARCH_FORM_TABS.flight
  }

  const parsedTabIndex = Number.parseInt(previousCategory, 10)
  if (Number.isNaN(parsedTabIndex)) {
    return WINGIE_SEARCH_FORM_TABS.flight
  }

  if (parsedTabIndex === WINGIE_SEARCH_FORM_TABS.hotel && hotelTabEnabled) {
    return WINGIE_SEARCH_FORM_TABS.hotel
  }

  if (parsedTabIndex === WINGIE_SEARCH_FORM_TABS.carRental && carRentalTabEnabled) {
    return WINGIE_SEARCH_FORM_TABS.carRental
  }

  return WINGIE_SEARCH_FORM_TABS.flight
}
